/**
 * The document title, kept in step with the route the shell is rendering.
 *
 * It reads the same trail the breadcrumb does, so a screen names itself the
 * same way in a browser tab as it does on the page, and a route that has named
 * its crumb has nothing else to remember.
 *
 * @module components/shell/RouteTitle
 */

import { useEffect } from "react";

import { useMatches } from "react-router";

import { composePageTitle, type PageTitleOptions } from "./page-title";
import { useRouteCrumbs } from "./RouteBreadcrumb";

/** What a route may put in its `handle` when its crumb is not what a tab reads. */
export interface RouteTitleHandle {
  title?: string;
}

/** @param title - What the browser tab should read. */
export function useDocumentTitle(title: string): void {
  useEffect(() => {
    document.title = title;
  }, [title]);
}

/**
 * @param options - Whether this session is impersonating someone.
 * @returns The title it set, so a test can read it without the document.
 */
export function useRouteTitle(options: PageTitleOptions = {}): string {
  const crumbs = useRouteCrumbs();
  const matches = useMatches();
  const override = matches.reduce<string | undefined>(
    (found, match) => (match.handle as RouteTitleHandle | undefined)?.title ?? found,
    undefined,
  );
  const labels = crumbs.map((crumb) => crumb.label);
  // An override replaces the page, which is the last crumb, and keeps whatever
  // context the trail above it names.
  const title = composePageTitle(
    override === undefined ? labels : [...labels.slice(0, -1), override],
    options,
  );
  useDocumentTitle(title);
  return title;
}

/**
 * Mounted once inside the shell, beside the breadcrumb.
 *
 * @example
 * ```tsx
 * <RouteTitle impersonating={Boolean(session.impersonatedBy)} />
 * ```
 */
export function RouteTitle({ impersonating = false }: PageTitleOptions): null {
  useRouteTitle({ impersonating });
  return null;
}
